import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";

import { getWorkspaceItems } from "../services/workspaceService";

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Planning: "bg-yellow-100 text-yellow-700",
  Completed: "bg-blue-100 text-blue-700",
  "On Hold": "bg-red-100 text-red-700",
};

const priorityStyles = {
  Critical: "bg-red-100 text-red-700",
  High: "bg-orange-100 text-orange-700",
  Medium: "bg-yellow-100 text-yellow-700",
  Low: "bg-green-100 text-green-700",
};

const WorkspaceItems = ({
  workspace,
  onAssignTask,
  refreshKey = 0,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const workspaceUuid =
    workspace?.uuid ||
    workspace?.workspace_uuid ||
    workspace?.id;

  /**
   * ==========================================================
   * FETCH WORKSPACE ITEMS
   * ==========================================================
   */
  const fetchItems = async () => {
    if (!workspaceUuid) {
      console.warn("Workspace UUID not available");
      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log(
        "========== FETCH WORKSPACE ITEMS =========="
      );

      console.log("WORKSPACE UUID:", workspaceUuid);
      console.log("REFRESH KEY:", refreshKey);

      const response =
        await getWorkspaceItems(workspaceUuid);

      console.log(
        "WORKSPACE ITEMS API RESPONSE:",
        response
      );

      /**
       * Handle different possible API response formats
       *
       * 1. [ ... ]
       * 2. { data: [ ... ] }
       * 3. { data: { data: [ ... ] } }
       */
      let itemList = [];

      if (Array.isArray(response)) {
        itemList = response;
      } else if (Array.isArray(response?.data)) {
        itemList = response.data;
      } else if (
        Array.isArray(response?.data?.data)
      ) {
        itemList = response.data.data;
      }

      console.log("FINAL ITEMS:", itemList);

      setItems(itemList);
    } catch (error) {
      console.error(
        "FETCH WORKSPACE ITEMS ERROR:",
        error
      );

      setError(
        error.response?.data?.message ||
          "Failed to load workspace items."
      );

      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * ==========================================================
   * FETCH ON:
   *
   * 1. Workspace change
   * 2. refreshKey change
   *
   * refreshKey changes after item creation.
   * ==========================================================
   */
  useEffect(() => {
    fetchItems();
  }, [workspaceUuid, refreshKey]);

  if (!workspace) {
    return null;
  }

  return (
    <div className="w-full">

      {/* =====================================================
          HEADER
          ===================================================== */}

      <div className="mb-6 flex items-center justify-between gap-4">

        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Workspace Items
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Modules and tasks available in this workspace
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">

          {/* ITEM COUNT */}

          <span className="rounded-lg bg-blue-50 px-4 py-2 font-semibold text-blue-600">
            {items.length} Items
          </span>

          {/* CREATE ITEM */}

          <button
            type="button"
            onClick={onAssignTask}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <Plus size={17} />

            Create Item
          </button>

        </div>

      </div>

      {/* =====================================================
          ERROR
          ===================================================== */}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* =====================================================
          LOADING
          ===================================================== */}

      {loading && (
        <div className="flex items-center justify-center py-10">

          <div className="flex items-center gap-2 text-sm text-slate-500">

            <Loader2
              size={20}
              className="animate-spin"
            />

            Loading items...

          </div>

        </div>
      )}

      {/* =====================================================
          TABLE
          ===================================================== */}

      {!loading && (
        <div className="w-full overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">

          <table className="w-full table-auto">

            {/* =================================================
                TABLE HEADER
                ================================================= */}

            <thead className="bg-slate-50">

              <tr>

                <th className="w-[40%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Module
                </th>

                <th className="w-[20%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Estimated Hours
                </th>

                <th className="w-[20%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Priority
                </th>

                <th className="w-[20%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Status
                </th>

              </tr>

            </thead>

            {/* =================================================
                TABLE BODY
                ================================================= */}

            <tbody>

              {items.length > 0 ? (

                items.map((item, index) => (

                  <tr
                    key={
                      item.uuid ||
                      item.item_uuid ||
                      item.id ||
                      index
                    }
                    className="border-t border-slate-200 transition hover:bg-slate-50"
                  >

                    {/* MODULE */}

                    <td className="px-4 py-3">

                      <div className="min-w-0">

                        <h4 className="truncate text-sm font-semibold text-slate-800">
                          {item.title ||
                            item.name ||
                            item.item_name ||
                            item.module_name ||
                            "-"}
                        </h4>

                        {item.description && (
                          <p className="mt-0.5 max-w-sm truncate text-xs text-slate-500">
                            {item.description}
                          </p>
                        )}

                      </div>

                    </td>

                    {/* ESTIMATED HOURS */}

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">

                      {item.estimated_hours ??
                        item.estimatedHours ??
                        0}{" "}
                      hrs

                    </td>

                    {/* PRIORITY */}

                    <td className="whitespace-nowrap px-4 py-3">

                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          priorityStyles[
                            item.priority
                          ] ||
                          "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {item.priority || "-"}
                      </span>

                    </td>

                    {/* STATUS */}

                    <td className="whitespace-nowrap px-4 py-3">

                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          statusStyles[
                            item.status
                          ] ||
                          "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {item.status || "-"}
                      </span>

                    </td>

                  </tr>

                ))

              ) : (

                <tr>

                  <td
                    colSpan="4"
                    className="px-4 py-8 text-center text-sm text-slate-500"
                  >
                    No items available in this workspace.
                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </div>
      )}

    </div>
  );
};

export default WorkspaceItems;